/**
 * URL safety helpers (Audit H2).
 *
 * The chat agent emits `citation.url`, pill URLs, portrait URLs etc.
 * downstream of user-controlled query text — prompt injection can therefore
 * land arbitrary strings into these fields. Modern browsers block most
 * `javascript:` URLs in `window.open` / `<a href>` activation, but Safari
 * historically permits it for synchronous user-gesture calls and the broader
 * universe of weird schemes (`data:`, `vbscript:`, `file:`) is still a real
 * surface. Defence-in-depth: validate every model-supplied URL before
 * handing it to a sink.
 */

const SAFE_NAV_PROTOCOLS  = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const SAFE_IMG_PROTOCOLS  = new Set(['http:', 'https:', 'data:']);

function parseUrl(raw: string): URL | null {
  try {
    // Resolve against the current origin so relative paths work; absolute
    // URLs ignore the base.
    return new URL(raw, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
  } catch {
    return null;
  }
}

/**
 * Returns the URL string only if the protocol is in the navigation allowlist
 * (http/https/mailto/tel). Returns `null` for `javascript:`, `data:`, `vbscript:`,
 * file:, weird schemes, or unparsable input. Caller decides what to do — usually
 * a no-op + console.warn.
 */
export function safeNavUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const url = parseUrl(raw);
  if (!url) return null;
  return SAFE_NAV_PROTOCOLS.has(url.protocol) ? raw : null;
}

/**
 * Same as safeNavUrl but for `<img src>` — adds `data:` to the allowlist
 * (legitimately used for inline thumbnails) and rejects `javascript:` etc.
 */
export function safeImgUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const url = parseUrl(raw);
  if (!url) return null;
  return SAFE_IMG_PROTOCOLS.has(url.protocol) ? raw : null;
}
